import React from 'react'
import { useSetupWizard } from '../../contexts/SetupWizardContext'
import { useThemeColors } from '../../hooks/useThemeColors'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline'

export default function WizardNavigation() {
  const { 
    currentStep, 
    totalSteps, 
    canProgress, 
    nextStep, 
    previousStep,
    completeSetup,
    isLoading 
  } = useSetupWizard()
  const colors = useThemeColors()

  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === totalSteps - 1

  const handleNext = async () => {
    if (isLastStep) {
      await completeSetup()
    } else {
      nextStep()
    }
  }

  return (
    <div className="wizard-navigation" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '2rem',
      gap: '1rem'
    }}>
      {/* Previous Button */}
      <button
        onClick={previousStep}
        disabled={isFirstStep}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: 'transparent',
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          color: isFirstStep ? colors.textMuted : colors.text,
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: isFirstStep ? 'not-allowed' : 'pointer',
          opacity: isFirstStep ? 0.5 : 1,
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (!isFirstStep) {
            e.currentTarget.style.borderColor = colors.primary
            e.currentTarget.style.color = colors.primary
          }
        }}
        onMouseLeave={(e) => {
          if (!isFirstStep) {
            e.currentTarget.style.borderColor = colors.border
            e.currentTarget.style.color = colors.text
          }
        }}
      >
        <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
        Previous
      </button>

      {/* Skip Button (for optional steps) */}
      {(currentStep === 5 || currentStep === 6) ? (
        <button
          onClick={nextStep}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'transparent',
            border: 'none',
            color: colors.textMuted,
            fontSize: '0.875rem',
            cursor: 'pointer',
            textDecoration: 'underline',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = colors.primary
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = colors.textMuted
          }}
        >
          Skip this step
        </button>
      ) : (
        <div />
      )}

      {/* Next/Complete Button */}
      <button
        onClick={handleNext}
        disabled={!canProgress || isLoading}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: canProgress && !isLoading ? colors.primary : colors.border,
          border: 'none',
          borderRadius: '8px',
          color: canProgress && !isLoading ? colors.background : colors.textMuted,
          fontSize: '0.875rem',
          fontWeight: '600',
          cursor: canProgress && !isLoading ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease',
          transform: 'scale(1)'
        }}
        onMouseEnter={(e) => {
          if (canProgress && !isLoading) {
            e.currentTarget.style.backgroundColor = colors.primaryHover
            e.currentTarget.style.transform = 'scale(1.02)'
          }
        }}
        onMouseLeave={(e) => {
          if (canProgress && !isLoading) {
            e.currentTarget.style.backgroundColor = colors.primary
            e.currentTarget.style.transform = 'scale(1)'
          }
        }}
      >
        {isLoading ? (
          <>
            <div className="spinner" />
            Processing...
          </>
        ) : isLastStep ? (
          'Launch My Facility'
        ) : currentStep === 0 ? (
          'Get Started'
        ) : (
          <>
            Next
            <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
          </>
        )}
      </button>

      <style>{`
        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .wizard-navigation button {
            padding: 0.625rem 1rem !important;
            font-size: 0.813rem !important;
          }
        }
      `}</style>
    </div>
  )
}